import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { FeatureConfigProvider } from "@/contexts/FeatureConfigContext";
import { GlobalPresenceProvider } from "@/contexts/GlobalPresenceContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { StudentViewProvider } from "@/contexts/StudentViewContext";
import { StudentViewBanner } from "@/components/StudentViewBanner";
import AudioUnlockListener from "@/components/AudioUnlockListener";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { CommandPalette } from "@/components/CommandPalette";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { usePWAUpdate } from "./hooks/usePWAUpdate";
import { RefreshAppButton } from "./components/RefreshAppButton";
import { Loader2 } from "lucide-react";

// Critical path components - keep static for fast initial load
import Landing from "./components/Landing";
import Auth from "./pages/Auth";
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Lazy load all other pages for code splitting
const Demo = lazy(() => import("./pages/Demo"));
const DemoCompetitions = lazy(() => import("./pages/DemoCompetitions"));
const DemoSalesPrograms = lazy(() => import("./pages/DemoSalesPrograms"));
const DemoEnterprise = lazy(() => import("./pages/DemoEnterprise"));
const DashboardRouter = lazy(() => import("./components/DashboardRouter"));
const Practice = lazy(() => import("./pages/Practice"));
const ObjectionPractice = lazy(() => import("./pages/ObjectionPractice"));
const ObjectionDrillDetail = lazy(() => import("./pages/ObjectionDrillDetail"));
const SessionDetail = lazy(() => import("./pages/SessionDetail"));
const Sessions = lazy(() => import("./pages/Sessions"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Leaderboards = lazy(() => import("./pages/Leaderboards"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Competitions = lazy(() => import("./pages/Competitions"));
const CompetitionDetail = lazy(() => import("./pages/CompetitionDetail"));
const CompetitionWaitingRoom = lazy(() => import("./pages/CompetitionWaitingRoom"));
const CompetitionLeaderboard = lazy(() => import("./pages/CompetitionLeaderboard"));
const JobBoard = lazy(() => import("./pages/JobBoard"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const RecruitersList = lazy(() => import("./pages/RecruitersList"));
const RecruiterProfile = lazy(() => import("./pages/RecruiterProfile"));
const MyApplications = lazy(() => import("./pages/MyApplications"));
const QA = lazy(() => import("./pages/QA"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const ForSalesPrograms = lazy(() => import("./pages/ForSalesPrograms"));
const ForEnterprise = lazy(() => import("./pages/ForEnterprise"));
const ForCompetitions = lazy(() => import("./pages/ForCompetitions"));
const MarketingIntegrations = lazy(() => import("./pages/MarketingIntegrations"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FerpaCompliance = lazy(() => import("./pages/FerpaCompliance"));
const Soc2Readiness = lazy(() => import("./pages/Soc2Readiness"));
const Teams = lazy(() => import("./pages/Teams"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard"));
const TeamGroupDetail = lazy(() => import("./pages/TeamGroupDetail"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminApplications = lazy(() => import("./pages/AdminApplications"));

const Marketing = lazy(() => import("./pages/Marketing"));
const MarketingDashboard = lazy(() => import("./pages/MarketingDashboard"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const BuyerPrep = lazy(() => import("./pages/BuyerPrep"));
const BuyerPracticePage = lazy(() => import("./pages/BuyerPracticePage"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const SpeedSell = lazy(() => import("./pages/SpeedSell"));
const ColdCall = lazy(() => import("./pages/ColdCall"));
const Negotiation = lazy(() => import("./pages/Negotiation"));
const SpeedSellFeedback = lazy(() => import("./pages/SpeedSellFeedback"));
const Connections = lazy(() => import("./pages/Connections"));
const Messages = lazy(() => import("./pages/Messages"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const ProfileSetupPage = lazy(() => import("./pages/ProfileSetupPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Usage = lazy(() => import("./pages/Usage"));
const GradeRecording = lazy(() => import("./pages/GradeRecording"));
const GradedRecordingFeedback = lazy(() => import("./pages/GradedRecordingFeedback"));
const MyFirefliesCalls = lazy(() => import("./pages/MyFirefliesCalls"));
const MyGongCalls = lazy(() => import("./pages/MyGongCalls"));
const AllCalls = lazy(() => import("./pages/AllCalls"));
const Mentors = lazy(() => import("./pages/Mentors"));
const Integrations = lazy(() => import("./pages/Integrations"));
const VerifyStudent = lazy(() => import("./pages/VerifyStudent"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const AICalling = lazy(() => import("./pages/AICalling"));
const Explainer = lazy(() => import("./pages/Explainer"));
const CallAnalytics = lazy(() => import("./pages/CallAnalytics"));
const BigEScoreInfo = lazy(() => import("./pages/BigEScoreInfo"));
const ResumeMatch = lazy(() => import("./pages/ResumeMatch"));
const Branding = lazy(() => import("./pages/Branding"));
const Employment = lazy(() => import("./pages/Employment"));
const NoLicense = lazy(() => import("./pages/NoLicense"));
const AdminLicenses = lazy(() => import("./pages/AdminLicenses"));
const JoinLicense = lazy(() => import("./pages/JoinLicense"));
const LicensePaid = lazy(() => import("./pages/LicensePaid"));

const PaySeat = lazy(() => import("./pages/PaySeat"));
const SeatPaid = lazy(() => import("./pages/SeatPaid"));






// OPTIMIZATION: Configure QueryClient for better performance under high load
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Increase staleTime to reduce unnecessary refetches
      staleTime: 60000, // 1 minute - data considered fresh
      gcTime: 5 * 60 * 1000, // 5 minutes cache retention
      // Disable aggressive refetching behaviors that cause load spikes
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      // Reduce retry attempts to prevent cascading failures
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

const PWAUpdateHandler = () => {
  usePWAUpdate();
  return null;
};

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <ProfileProvider>
          <StudentViewProvider>
          <GlobalPresenceProvider>
            <FeatureConfigProvider>
            <TooltipProvider>
            <BrowserRouter>
              <ErrorBoundary>
                <StudentViewBanner />
                <ScrollToTop />
                <Toaster />
                <Sonner />
                <AudioUnlockListener />
                <PWAUpdateHandler />
                <RefreshAppButton />
                
                <CommandPalette />
                <FeedbackWidget />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public routes - Landing and Auth are static for fast initial load */}
                    <Route path="/" element={<Landing />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                    
                    {/* Lazy-loaded public routes */}
                    <Route path="/demo" element={<Demo />} />
                    <Route path="/demo/competitions" element={<DemoCompetitions />} />
                    <Route path="/demo/sales-programs" element={<DemoSalesPrograms />} />
                    <Route path="/demo/enterprise" element={<DemoEnterprise />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/for-sales-programs" element={<ForSalesPrograms />} />
                    <Route path="/for-enterprise" element={<ForEnterprise />} />
                    <Route path="/for-competitions" element={<ForCompetitions />} />
                    <Route path="/integrations-overview" element={<MarketingIntegrations />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/ferpa" element={<FerpaCompliance />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/soc2" element={<Soc2Readiness />} />
                    <Route path="/cost-calculator" element={<CostCalculator />} />
                    <Route path="/explainer" element={<Explainer />} />
                    <Route path="/u/:userId" element={<PublicProfile />} />
                    <Route path="/accept-invitation" element={<AcceptInvitation />} />
                    <Route path="/branding" element={<Branding />} />
                    <Route path="/employment" element={<Employment />} />

                    {/* Protected routes - all lazy loaded */}





                    <Route path="/onboarding" element={<ProtectedRoute skipOnboardingCheck><OnboardingPage /></ProtectedRoute>} />
                    <Route path="/profile-setup" element={<ProtectedRoute skipOnboardingCheck><ProfileSetupPage /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
                    <Route path="/professor-dashboard" element={<Navigate to="/dashboard?tab=students" replace />} />
                    <Route path="/practice/:scenarioId" element={<ProtectedRoute><Practice /></ProtectedRoute>} />
                    <Route path="/objection-practice/:scenarioId" element={<ProtectedRoute><ObjectionPractice /></ProtectedRoute>} />
                    <Route path="/objection-drill/:drillId" element={<ProtectedRoute><ObjectionDrillDetail /></ProtectedRoute>} />
                    <Route path="/sessions" element={<ProtectedRoute><Sessions /></ProtectedRoute>} />
                    <Route path="/sessions/:sessionId" element={<ProtectedRoute><SessionDetail /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/leaderboards" element={<ProtectedRoute><Leaderboards /></ProtectedRoute>} />
                    <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
                    <Route path="/competitions" element={<ProtectedRoute><Competitions /></ProtectedRoute>} />
                    <Route path="/competitions/:competitionId" element={<ProtectedRoute><CompetitionDetail /></ProtectedRoute>} />
                    <Route path="/competitions/:competitionId/waiting-room" element={<ProtectedRoute><CompetitionWaitingRoom /></ProtectedRoute>} />
                    <Route path="/competitions/:competitionId/leaderboard" element={<ProtectedRoute><CompetitionLeaderboard /></ProtectedRoute>} />
                    <Route path="/jobs" element={<ProtectedRoute><JobBoard /></ProtectedRoute>} />
                    <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
                    <Route path="/recruiters" element={<ProtectedRoute><RecruitersList /></ProtectedRoute>} />
                    <Route path="/recruiters/:recruiterId" element={<ProtectedRoute><RecruiterProfile /></ProtectedRoute>} />
                    <Route path="/my-applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
                    <Route path="/qa" element={<ProtectedRoute><QA /></ProtectedRoute>} />
                    <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
                    <Route path="/teams/:teamId" element={<ProtectedRoute><TeamDashboard /></ProtectedRoute>} />
                    <Route path="/teams/:teamId/group/:groupId" element={<ProtectedRoute><TeamGroupDetail /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                    <Route path="/admin/applications" element={<ProtectedRoute><AdminApplications /></ProtectedRoute>} />
                    
                    <Route path="/admin/licenses" element={<ProtectedRoute><AdminLicenses /></ProtectedRoute>} />
                    <Route path="/join/:code" element={<ProtectedRoute skipOnboardingCheck skipLicenseCheck><JoinLicense /></ProtectedRoute>} />
                    <Route path="/join" element={<ProtectedRoute skipOnboardingCheck skipLicenseCheck><JoinLicense /></ProtectedRoute>} />
                    <Route path="/no-license" element={<ProtectedRoute skipOnboardingCheck skipLicenseCheck><NoLicense /></ProtectedRoute>} />
                    
                    <Route path="/license-paid" element={<ProtectedRoute skipOnboardingCheck skipLicenseCheck><LicensePaid /></ProtectedRoute>} />
                    <Route path="/license/:licenseId/pay" element={<PaySeat />} />
                    <Route path="/seat-paid" element={<SeatPaid />} />
                    <Route path="/marketing" element={<ProtectedRoute><Marketing /></ProtectedRoute>} />
                    <Route path="/marketing-dashboard" element={<ProtectedRoute><MarketingDashboard /></ProtectedRoute>} />
                    <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                    <Route path="/buyer-prep/:competitionId" element={<ProtectedRoute><BuyerPrep /></ProtectedRoute>} />
                    <Route path="/buyer-practice/:competitionId" element={<ProtectedRoute><BuyerPracticePage /></ProtectedRoute>} />
                    <Route path="/speed-sell" element={<ProtectedRoute><SpeedSell /></ProtectedRoute>} />
                    <Route path="/speed-sell/:scenarioId" element={<ProtectedRoute><SpeedSell /></ProtectedRoute>} />
                    <Route path="/speed-sell-feedback" element={<ProtectedRoute><SpeedSellFeedback /></ProtectedRoute>} />
                    <Route path="/cold-call" element={<ProtectedRoute><ColdCall /></ProtectedRoute>} />
                    <Route path="/cold-call/:scenarioId" element={<ProtectedRoute><ColdCall /></ProtectedRoute>} />
                    <Route path="/negotiation" element={<ProtectedRoute><Negotiation /></ProtectedRoute>} />
                    <Route path="/negotiation/:scenarioId" element={<ProtectedRoute><Negotiation /></ProtectedRoute>} />
                    <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
                    <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/messages/:partnerId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/usage" element={<ProtectedRoute><Usage /></ProtectedRoute>} />
                    <Route path="/grade-recording" element={<ProtectedRoute><GradeRecording /></ProtectedRoute>} />
                    <Route path="/graded-recording-feedback" element={<ProtectedRoute><GradedRecordingFeedback /></ProtectedRoute>} />
                    <Route path="/call-analytics" element={<ProtectedRoute><CallAnalytics /></ProtectedRoute>} />
                    <Route path="/resume-match" element={<ProtectedRoute><ResumeMatch /></ProtectedRoute>} />
                    <Route path="/my-fireflies-calls" element={<ProtectedRoute><MyFirefliesCalls /></ProtectedRoute>} />
                    <Route path="/my-gong-calls" element={<ProtectedRoute><MyGongCalls /></ProtectedRoute>} />
                    <Route path="/all-calls" element={<ProtectedRoute><AllCalls /></ProtectedRoute>} />
                    <Route path="/mentors" element={<ProtectedRoute><Mentors /></ProtectedRoute>} />
                    <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
                    <Route path="/verify-student" element={<ProtectedRoute><VerifyStudent /></ProtectedRoute>} />
                    <Route path="/ai-calling" element={<ProtectedRoute><AICalling /></ProtectedRoute>} />
                    <Route path="/bige-score-info" element={<ProtectedRoute><BigEScoreInfo /></ProtectedRoute>} />
                    <Route path="/bige-score-info" element={<ProtectedRoute><BigEScoreInfo /></ProtectedRoute>} />
                    
                    {/* 404 fallback */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              </BrowserRouter>
            </TooltipProvider>
          </FeatureConfigProvider>
          </GlobalPresenceProvider>
        </StudentViewProvider>
        </ProfileProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
